import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/dates";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { Briefcase, Users, FileClock, RotateCcw, Repeat, Clock, ShieldCheck, AlertTriangle } from "lucide-react";

const COLORS = ["#3b82f6", "#10b981", "#64748b", "#f59e0b", "#6366f1"];
const money = (n) => `$${(n || 0).toLocaleString("en-AU", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

export default function Reports() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { api.get("/reports").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="text-slate-400">Loading reports…</div>;

  const stat = (label, value, Icon, tone) => (
    <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-3 ${tone}`}><Icon className="w-6 h-6" /></div>
      <p className="text-3xl font-heading font-bold text-navy">{value}</p><p className="text-sm text-slate-500">{label}</p>
    </div>
  );

  return (
    <div className="space-y-6 animate-fade-up">
      <div><h1 className="text-3xl sm:text-4xl font-heading font-bold text-navy">Reports</h1><p className="text-slate-500 mt-1">Performance & invoicing overview</p></div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {stat("Total Jobs", data.total_jobs, Briefcase, "bg-blue-100 text-blue-700")}
        {stat("Total Customers", data.total_customers, Users, "bg-indigo-100 text-indigo-700")}
        {stat("Waiting for Invoice", data.waiting_invoice_jobs.length, FileClock, "bg-amber-100 text-amber-700")}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
        {stat("Return Visits Open", (data.return_visit_jobs || []).length, RotateCcw, "bg-orange-100 text-orange-700")}
        {stat("Avg Visits / Job", data.avg_visits_per_job ?? 0, Repeat, "bg-blue-100 text-blue-700")}
        {stat("Avg Job Duration (min)", data.avg_job_duration_minutes ?? 0, Clock, "bg-indigo-100 text-indigo-700")}
        {stat("Warranty Work", data.warranty_count ?? 0, ShieldCheck, "bg-emerald-100 text-emerald-700")}
        {stat("Defect Work", data.defect_count ?? 0, AlertTriangle, "bg-red-100 text-red-700")}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
          <h2 className="font-heading font-semibold text-navy mb-4">Jobs by Status</h2>
          {data.by_status.length === 0 ? <p className="text-slate-400 text-sm py-12 text-center">No data yet.</p> : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={data.by_status} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                  {data.by_status.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
          <h2 className="font-heading font-semibold text-navy mb-4">Jobs by Technician</h2>
          {data.by_technician.length === 0 ? <p className="text-slate-400 text-sm py-12 text-center">No data yet.</p> : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={data.by_technician}>
                <XAxis dataKey="name" tick={{ fontSize: 12 }} /><YAxis allowDecimals={false} /><Tooltip />
                <Bar dataKey="value" fill="#1e293b" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
        <h2 className="font-heading font-semibold text-navy mb-4">Charge Code Usage</h2>
        {(data.charge_code_usage || []).length === 0 ? <p className="text-slate-400 text-sm py-12 text-center">No charge codes recorded yet.</p> : (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data.charge_code_usage} layout="vertical" margin={{ left: 40 }}>
              <XAxis type="number" allowDecimals={false} /><YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 11 }} /><Tooltip />
              <Bar dataKey="value" fill="#3b82f6" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm" data-testid="builder-stats">
          <h2 className="font-heading font-semibold text-navy mb-1">Builder Statistics</h2>
          <p className="text-xs text-slate-400 mb-4">Charge Value = estimated from charge-code rates (office-only, excludes material cost/profit).</p>
          {(data.builder_stats || []).length === 0 ? <p className="text-slate-400 text-sm">No data yet.</p> : (
            <div className="divide-y divide-slate-100">
              {data.builder_stats.slice(0, 10).map((b, i) => (
                <div key={i} className="flex justify-between items-center py-2.5 text-sm">
                  <div><p className="font-medium text-navy">{b.name}</p><p className="text-xs text-slate-500">{b.jobs} job{b.jobs === 1 ? "" : "s"}</p></div>
                  <span className="font-semibold text-navy">{money(b.charge_value)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm" data-testid="tech-productivity">
          <h2 className="font-heading font-semibold text-navy mb-1">Technician Productivity</h2>
          <p className="text-xs text-slate-400 mb-4">Visits (dockets) completed and estimated charge value per technician.</p>
          {(data.tech_productivity || []).length === 0 ? <p className="text-slate-400 text-sm">No data yet.</p> : (
            <div className="divide-y divide-slate-100">
              {data.tech_productivity.map((t, i) => (
                <div key={i} className="flex justify-between items-center py-2.5 text-sm">
                  <div><p className="font-medium text-navy">{t.name}</p><p className="text-xs text-slate-500">{t.jobs} jobs · {t.visits} visits</p></div>
                  <span className="font-semibold text-navy">{money(t.charge_value)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
        <h2 className="font-heading font-semibold text-navy mb-4">Jobs Ready for Invoicing</h2>
        {data.waiting_invoice_jobs.length === 0 ? <p className="text-slate-400 text-sm">No jobs waiting.</p> : (
          <div className="divide-y divide-slate-100">
            {data.waiting_invoice_jobs.map((j) => (
              <div key={j.id} onClick={() => navigate(`/jobs/${j.id}`)} data-testid={`invoice-job-${j.id}`} className="flex justify-between items-center py-3 cursor-pointer hover:bg-slate-50 px-2 rounded-lg">
                <div><p className="font-medium text-navy">{j.customer_name}</p><p className="text-xs text-slate-500">{j.job_number} · {formatDate(j.appointment_date)}</p></div>
                <span className="text-xs bg-amber-100 text-amber-700 px-3 py-1 rounded-full font-medium">Ready</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
